import { formatShipmentStatus } from '#/entities/shipment'
import type {
  ShipmentDetail,
  StopResponse,
} from '#/shared/api/generated/shipment/models'
import { StopType } from '#/shared/api/generated/shipment/models'

import type { CarrierRequestsOrderSummary } from './types'

function toFiniteNumber(
  raw: number | string | null | undefined,
): number | null {
  if (raw == null || raw === '') return null
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : null
}

function findStop(
  stops: StopResponse[] | undefined,
  type: StopType,
): StopResponse | undefined {
  return stops?.find((stop) => stop.type === type)
}

function formatStopLocation(stop: StopResponse | undefined): string {
  if (!stop) return ''
  const parts = [stop.city, stop.state, stop.zip].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  )
  if (parts.length === 0) return ''
  const cityState = [parts[0], parts[1]].filter(Boolean).join(', ')
  return parts[2] ? `${cityState} ${parts[2]}` : cityState
}

function vehicleLabel(shipment: ShipmentDetail): string | undefined {
  const vehicle = shipment.vehicles?.[0]
  if (!vehicle) return undefined
  return `${vehicle.year} ${vehicle.make} ${vehicle.model}`
}

function pickupAtFor(
  pickup: StopResponse | undefined,
  shipment: ShipmentDetail,
): string | undefined {
  return pickup?.scheduled_at ?? shipment.estimated_pickup_at ?? undefined
}

function deliveryAtFor(
  delivery: StopResponse | undefined,
  shipment: ShipmentDetail,
): string | undefined {
  return delivery?.scheduled_at ?? shipment.estimated_delivery_at ?? undefined
}

export function mapShipmentToOrderSummary(
  shipment: ShipmentDetail,
): CarrierRequestsOrderSummary {
  const pickup = findStop(shipment.stops, StopType.pickup)
  const delivery = findStop(shipment.stops, StopType.delivery)
  return {
    orderNumber: shipment.code,
    // Raw enum (`not_signed`) is never shown — same humanized label the rest of
    // the app uses for a shipment status.
    status: formatShipmentStatus(shipment.status),
    origin: formatStopLocation(pickup),
    destination: formatStopLocation(delivery),
    vehicleLabel: vehicleLabel(shipment),
    vehicleType: shipment.vehicles?.[0]?.type,
    pickupAt: pickupAtFor(pickup, shipment),
    deliveryAt: deliveryAtFor(delivery, shipment),
    totalTariff: toFiniteNumber(shipment.tariff) ?? undefined,
    carrierPay: toFiniteNumber(shipment.carrier_pay) ?? undefined,
    paymentMethod: shipment.payment_method ?? undefined,
    paymentTerms: shipment.payment_terms ?? undefined,
    postedAt: shipment.dates?.posted ?? shipment.created_at,
  }
}
